import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentVariant } from '../types';

@Component({
  selector: 'app-alert',
  imports: [CommonModule],
  template: `
    <div 
      [class]="alertClasses"
      [attr.role]="'alert'"
      [attr.aria-live]="dismissible() ? 'polite' : 'assertive'">
      <div class="flex items-start gap-3">
        @if (showIcon()) {
          <div [class]="iconClasses">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" [attr.d]="getIconPath()" clip-rule="evenodd" />
            </svg>
          </div>
        }
        <div class="flex-1">
          @if (title()) {
            <h4 [class]="titleClasses">{{ title() }}</h4>
          }
          <div [class]="messageClasses">
            <ng-content></ng-content>
          </div>
        </div>
        @if (dismissible()) {
          <button
            type="button"
            (click)="dismiss()"
            class="p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label="Dismiss alert">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        }
      </div>
    </div>
  `,
  styles: []
})
export class AlertComponent {
  variant = input<ComponentVariant>('info');
  title = input<string>('');
  dismissible = input<boolean>(false);
  showIcon = input<boolean>(true);
  outlined = input<boolean>(false);

  dismissEvent = output<void>();
  visible = signal(true);

  dismiss(): void {
    this.visible.set(false);
    this.dismissEvent.emit();
  }

  get alertClasses(): string {
    if (!this.visible()) return 'hidden';

    const classes = ['rounded-lg', 'p-4', 'border', 'transition-all', 'duration-200'];

    if (this.outlined()) {
      classes.push('bg-transparent', 'border-2');
      const variantMap: Record<ComponentVariant, string> = {
        success: 'border-green-500 text-green-700 dark:text-green-400',
        error: 'border-red-500 text-red-700 dark:text-red-400',
        warning: 'border-yellow-500 text-yellow-700 dark:text-yellow-400',
        info: 'border-blue-500 text-blue-700 dark:text-blue-400',
        primary: 'border-blue-600 text-blue-700 dark:text-blue-400',
        secondary: 'border-gray-500 text-gray-700 dark:text-gray-400',
        danger: 'border-red-500 text-red-700 dark:text-red-400'
      };
      classes.push(variantMap[this.variant()] || variantMap.info);
    } else {
      const variantMap: Record<ComponentVariant, string> = {
        success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
        error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
        warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
        info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
        primary: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
        secondary: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200',
        danger: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
      };
      classes.push(variantMap[this.variant()] || variantMap.info);
    }

    return classes.join(' ');
  }

  get iconClasses(): string {
    return 'flex-shrink-0';
  }

  get titleClasses(): string {
    return 'font-semibold mb-1';
  }

  get messageClasses(): string {
    return 'text-sm';
  }

  getIconPath(): string {
    const iconMap: Record<ComponentVariant, string> = {
      success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
      error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
      warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
      info: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      primary: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      secondary: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
      danger: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
    };
    return iconMap[this.variant()] || iconMap.info;
  }
}

